import anime from 'animejs';
import { live } from '../data/live';
window.confetti = require('canvas-confetti');

var decodeEntities = (function () {
    // Avoids re-creating the element on every call.
    const element = document.createElement('div');
    return function decodeHTMLEntities(str) {
        if (str && typeof str === 'string') {
            str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
            str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
            element.innerHTML = str;
            str = element.textContent;
            element.textContent = '';
        }
        return str;
    };
})();

/**
 * Everything with a euro sign on it: the running total in the bar and on the break
 * screen, the donation goal bar, and the two break-screen leaderboards.
 */
export const donationsRenderer = {
    totalDonationsCounter: 0,
    oldGoalDonationPercent: 0,
    goalDonationPercent: 0,
    goalDonationPercentCounter: 0,
    surpriseDeployed: false,
    goalDonationLeftDefault: -1192,
    goalDonationLeft: -1192,
    shownDonationGoalProgressBar: false,
    isAnimatingDonationGoalProgressBar: false,
    shouldResetDonationGoalPosition: false,
    shouldUpdateDonationGoal: false,
    animateTotal: (donationsTotal) => {
        const el = document.getElementById('donation-amount-number');
        const elShadow = document.getElementById('donation-amount-number-shadow');
        const breakDonationAmount = document.querySelector('#break-donations .donation-total');
        anime({
            targets: donationsRenderer,
            totalDonationsCounter: donationsTotal,
            round: 100,
            easing: 'easeOutExpo',
            update: function () {
                let formattedNumber = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(donationsRenderer.totalDonationsCounter);
                el.innerHTML = formattedNumber;
                elShadow.innerHTML = formattedNumber;
                breakDonationAmount.innerHTML = formattedNumber;
            },
        });
    },

    updateDonations: () => {
        donationsRenderer.animateTotal(live.getTotalDonationsWithOffset());
    },

    updateDonationReason: () => {
        document.getElementById('donation-reason').innerHTML = live.getDonationReason();
        document.querySelector('#break-donations .donation-reason').innerHTML = live.getDonationReason();
    },

    /** What the three donor rows currently show, so a no-op poll animates nothing. */
    renderedDonors: [],

    donorRows: () => [1, 2, 3].map((n) => ({
        name: document.querySelector(`.donator-leaderboard .leaderboard-${n} .name`),
        amount: document.querySelector(`.donator-leaderboard .leaderboard-${n} .amount`),
    })),

    /**
     * Renders the whole TOP DONATORI board from one list, in one pass.
     *
     * It used to be three independent updates staggered 1.5 s apart, each shifting its
     * own slice of rows down and inserting at the top. On a rank swap that left a row
     * holding stale content copied from the row above — the same donor showing twice —
     * until that row's own turn came round three seconds later. Rendering all three rows
     * from the same source list cannot produce that.
     */
    updateDonatorsLeaderboard: () => {
        const donors = live.getDonatorsLeaderboard();
        const money = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
        const next = [0, 1, 2].map((i) => donors[i]
            ? { name: donors[i].name ?? '', amount: money(donors[i].total) }
            : { name: '', amount: '' });

        const shown = donationsRenderer.renderedDonors;
        if (next.every((row, i) => shown[i]?.name === row.name && shown[i]?.amount === row.amount)) return;

        // A donation that moves nobody is just a number ticking up: write it and stay
        // still. Reordering the board is what earns the animation.
        const reordered = next.some((row, i) => (shown[i]?.name ?? '') !== row.name);
        donationsRenderer.renderedDonors = next;

        const rows = donationsRenderer.donorRows();
        const write = (i) => {
            rows[i].name.innerHTML = next[i].name;
            rows[i].amount.innerHTML = next[i].amount;
        };

        if (!reordered) {
            rows.forEach((_, i) => write(i));
            return;
        }

        // Fade every row out, write all three at once while none is visible, fade back in.
        // Writing each row at its own midpoint left a 120 ms window in which the row above
        // already showed its new donor and this one still showed the old copy of it.
        const targets = rows.flatMap((row) => [row.name, row.amount]);
        const cascade = (el, i) => Math.floor(i / 2) * 120;   // per row, not per element
        let written = false;
        const writeAll = () => {
            if (written) return;
            written = true;
            rows.forEach((_, i) => write(i));
        };

        anime.timeline({ easing: 'easeInOutQuad', complete: writeAll })   // in case it is cut short
            .add({ targets, opacity: 0, translateX: 20, duration: 200, delay: cascade, complete: writeAll })
            .add({ targets, opacity: 1, translateX: 0, duration: 200, delay: cascade }, '+=60');
    },

    maybeUpdateDonationGoal: () => {
        if (!donationsRenderer.shouldUpdateDonationGoal) return;

        donationsRenderer.updateDonationGoal();
    },

    updateDonationGoal: () => {
        donationsRenderer.maybeResetDonationGoalPosition();
        const flairObjectiveEl = document.querySelector('#donation-progress .flair-objective');
        flairObjectiveEl.innerHTML = live.getDonationGoalFlair();

        const flairObjectiveAmountEl = document.querySelector('#donation-progress .donation-totals');
        const donationGoalAmount = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(live.getDonationGoalAmount());
        const totalDonations = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(live.getTotalDonationsWithOffset());
        flairObjectiveAmountEl.innerHTML = totalDonations + ' / ' + donationGoalAmount;

        donationsRenderer.goalDonationPercent = live.getTotalDonationsWithOffset() * 100 / live.getDonationGoalAmount();
        if (donationsRenderer.goalDonationPercent > 100) donationsRenderer.goalDonationPercent = 100;

        const minX = donationsRenderer.goalDonationLeftDefault;
        const toAdd = 4;
        let currentLeft = minX * donationsRenderer.goalDonationPercent / 100;
        currentLeft -= toAdd;
        donationsRenderer.goalDonationLeft = minX - currentLeft;
        if (donationsRenderer.shownDonationGoalProgressBar) {
            donationsRenderer.animateDonationGoalProgressBar();
        }
    },

    getGoalAnimationDuration: () => {
        return (donationsRenderer.goalDonationPercent - donationsRenderer.oldGoalDonationPercent) * 50;
    },

    enableUpdatingDonationGoal: () => {
        donationsRenderer.shouldUpdateDonationGoal = true;
        donationsRenderer.maybeUpdateDonationGoal();
    },

    disenableUpdatingDonationGoal: () => {
        donationsRenderer.shouldUpdateDonationGoal = false;
    },

    resetDonationGoalPosition: () => {
        // console.log("Resetting goal bar position");
        donationsRenderer.shownDonationGoalProgressBar = false;
        donationsRenderer.goalDonationPercent = 0;
        donationsRenderer.oldGoalDonationPercent = 0;
        donationsRenderer.goalDonationPercentCounter = 0;
        document.querySelector('#donation-progress .donation-bar-progressbar').style.left = donationsRenderer.goalDonationLeftDefault + 'px';
    },

    maybeResetDonationGoalPosition: () => {
        if (donationsRenderer.shouldResetDonationGoalPosition) {
            donationsRenderer.resetDonationGoalPosition();
            donationsRenderer.shouldResetDonationGoalPosition = false;
        }
    },

    animateDonationGoalProgressBar: () => {
        if (donationsRenderer.shouldResetDonationGoalPosition) return;
        if (donationsRenderer.isAnimatingDonationGoalProgressBar) return;
        if (donationsRenderer.goalDonationPercent === donationsRenderer.oldGoalDonationPercent) return;

        donationsRenderer.isAnimatingDonationGoalProgressBar = true;
        const flairPercentEl = document.querySelector('#donation-progress .donation-bar-progressbar .percent');
        const currentGoalDonationPercent = donationsRenderer.goalDonationPercent;
        anime({
            targets: donationsRenderer,
            goalDonationPercentCounter: donationsRenderer.goalDonationPercent,
            round: 10,
            easing: 'easeOutCirc',
            duration: donationsRenderer.getGoalAnimationDuration(),
            delay: 1000,
            update: function () {
                flairPercentEl.innerHTML = parseInt(donationsRenderer.goalDonationPercentCounter) + '%';
            },
            complete: function () {
                donationsRenderer.shownDonationGoalProgressBar = true;
                donationsRenderer.isAnimatingDonationGoalProgressBar = false;
                donationsRenderer.oldGoalDonationPercent = currentGoalDonationPercent;
            }
        });
        anime({
            targets: '#donation-progress .donation-bar-progressbar',
            left: donationsRenderer.goalDonationLeft,
            easing: 'easeOutCirc',
            duration: donationsRenderer.getGoalAnimationDuration(),
            delay: 1000,
            complete: function () {
                if (!live.getDonationDeploySurprise()) return;
                if (!donationsRenderer.shouldUpdateDonationGoal) return;
                if (donationsRenderer.surpriseDeployed) return;
                if (donationsRenderer.goalDonationPercent >= 100) {
                    window.confetti.default({
                        particleCount: 200,
                        spread: 200,
                        origin: { y: 0.2, x: 0.6 },
                        zIndex: 50,
                    });
                    window.confetti.default({
                        particleCount: 200,
                        spread: 150,
                        origin: { y: 0.2, x: 0.4 },
                        zIndex: 50,
                    });
                    window.confetti.default({
                        particleCount: 200,
                        spread: 140,
                        origin: { y: 0.2 },
                        zIndex: 50,
                    });
                    donationsRenderer.surpriseDeployed = true;
                }
            }
        });
    },

    /** Resolved when the goal bar has finished its stay on screen. */
    goalFinished: undefined,

    /** Segment wrapper: shows the bar, resolves when it retracts. */
    playDonationGoal: () => {
        const done = new Promise((resolve) => { donationsRenderer.goalFinished = resolve; });
        donationsRenderer.showDonationGoalProgressBar();
        return done;
    },

    showDonationGoalProgressBar: () => {
        donationsRenderer.enableUpdatingDonationGoal();

        const donationGoalDuration = 10000;
        const donationGoalEnterAnimationDuration = 500;
        anime({
            targets: '#donation-progress',
            duration: donationGoalEnterAnimationDuration,
            opacity: 1,
            direction: 'alternate',
            easing: 'easeInOutExpo',
            endDelay: donationGoalDuration,
        });
        anime({
            targets: ['#donation-progress .donation-flair', '#donation-progress .donation-totals'],
            duration: donationGoalEnterAnimationDuration,
            top: 4,
            direction: 'alternate',
            easing: 'easeInOutExpo',
            endDelay: donationGoalDuration,
        });
        anime({
            targets: ['#donation-progress .donation-bar-container'],
            duration: donationGoalEnterAnimationDuration,
            bottom: 10,
            direction: 'alternate',
            easing: 'easeInOutExpo',
            endDelay: donationGoalDuration,
            complete: function () {
                donationsRenderer.shouldResetDonationGoalPosition = true;
                donationsRenderer.disenableUpdatingDonationGoal();

                donationsRenderer.goalFinished?.();
            }
        });
        donationsRenderer.animateDonationGoalProgressBar();
    },

    shownLeaderboard: false,

    maybeShowLeaderboard: () => {
        const leaderboardElement = document.querySelector('#donation-leaderboards');
        if (live.shouldShowLeaderboard()) {
            leaderboardElement.classList.remove('hidden');
            if (donationsRenderer.shownLeaderboard)
                return;

            anime({
                targets: leaderboardElement,
                duration: 5000,
                opacity: 1,
                easing: 'easeInOutExpo',
            });
        }
        else
            leaderboardElement.classList.add('hidden');
    },

    updateDonationLeaderboard: () => {
        const donationLeaderboard = live.getDonationLeaderboard();
        donationLeaderboard.forEach((d, i) => {
            const selector = `#break-donations .donation-leaderboard .leaderboard-${i + 1}`;
            const donationLeaderboardElement = document.querySelector(selector);
            donationLeaderboardElement.querySelector(".name").innerHTML = d.name;
            const formattedAmount = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(d.total);
            donationLeaderboardElement.querySelector(".amount").innerHTML = formattedAmount;
        })
    },

    startDonationLeaderboardToggleLoop: () => {
        const crossFadeDelay = 30000;
        const crossFadeDuration = 1500;
        anime({
            targets: '#break-donations .donation-leaderboard',
            opacity: 1,
            easing: 'easeInOutQuad',
            duration: crossFadeDuration,
            delay: crossFadeDelay / 2,
            endDelay: crossFadeDelay / 2,
            direction: 'alternate',
            loop: true,
        });
        anime({
            targets: '#break-donations .donator-leaderboard',
            opacity: 0,
            easing: 'easeInOutQuad',
            duration: crossFadeDuration,
            delay: crossFadeDelay / 2,
            endDelay: crossFadeDelay / 2,
            direction: 'alternate',
            loop: true,
        });
    },

    showBreakDonationContainer: () => {
        setTimeout(function () {
            anime({
                targets: ['#break-donations'],
                opacity: 1,
                easing: 'linear',
                duration: 1500,
            });
        }, 1000);
    },};

window.donationsRenderer = donationsRenderer;
window.anime = anime;
